import React, { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { MdCheck, MdClose } from "react-icons/md";
import { PaziaInput, PaziaSelect, IDropdownOption } from "components/atoms";
import { CourseTrainerApi } from "api/types";
import { useTrainers } from "api/hooks";
import { formatDate, toMoneyFormat } from "utils";
import { COURSE_TRAINER_COST_SCOPE, CreateTrainerCostEntryRequest } from "api/trainer-costs/trainerCostsApi";
import { buildRateOptions, calculateCost, isManualRateSelection, resolveRateType } from "./trainerRateCalculation";
import { groupPricesBySession } from "./sessionGrouping";

const SCOPE_OPTIONS: IDropdownOption[] = [
  { value: COURSE_TRAINER_COST_SCOPE.SESSION.toString(), label: "Per zjazd" },
  { value: COURSE_TRAINER_COST_SCOPE.DAY.toString(), label: "Per dzień" },
  { value: COURSE_TRAINER_COST_SCOPE.COURSE.toString(), label: "Cały kurs" },
];

const FIELDS = {
  TRAINER_ID: "trainerId",
  SCOPE_TYPE: "scopeType",
  COURSE_SESSION_ID: "courseSessionId",
  DATE: "date",
  RATE_SELECTION: "rateSelection",
  RATE_AMOUNT: "rateAmount",
} as const;

const schema = yup.object({
  [FIELDS.TRAINER_ID]: yup.number().required(),
  [FIELDS.SCOPE_TYPE]: yup.number().required(),
  [FIELDS.COURSE_SESSION_ID]: yup
    .number()
    .nullable()
    .when(FIELDS.SCOPE_TYPE, {
      is: (scopeType: number) => Number(scopeType) === COURSE_TRAINER_COST_SCOPE.SESSION,
      then: (shema) => shema.required(),
    }),
  [FIELDS.DATE]: yup
    .string()
    .nullable()
    .when(FIELDS.SCOPE_TYPE, {
      is: (scopeType: number) => Number(scopeType) === COURSE_TRAINER_COST_SCOPE.DAY,
      then: (shema) => shema.required(),
    }),
  [FIELDS.RATE_SELECTION]: yup.string().required(),
  [FIELDS.RATE_AMOUNT]: yup.number().required().moreThan(0),
});
type FormValues = yup.InferType<typeof schema>;

interface AddTrainerCostEntryInlineRowProps {
  trainers: CourseTrainerApi[];
  onSubmit: (data: CreateTrainerCostEntryRequest) => Promise<void>;
  onCancel: () => void;
}
export const AddTrainerCostEntryInlineRow = ({ trainers, onSubmit, onCancel }: AddTrainerCostEntryInlineRowProps) => {
  const { trainers: allTrainers } = useTrainers(0, 1_000_000);
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { scopeType: COURSE_TRAINER_COST_SCOPE.COURSE },
  });
  const { watch, handleSubmit, setValue } = methods;

  const trainerId = watch(FIELDS.TRAINER_ID);
  const scopeType = watch(FIELDS.SCOPE_TYPE);
  const courseSessionId = watch(FIELDS.COURSE_SESSION_ID);
  const date = watch(FIELDS.DATE);
  const rateSelection = watch(FIELDS.RATE_SELECTION);
  const rateAmount = watch(FIELDS.RATE_AMOUNT);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setValue(FIELDS.COURSE_SESSION_ID, null);
    setValue(FIELDS.DATE, null);
    // Whenever the trainer or scope changes, any previously picked session/day belongs to a
    // stale context and must not silently stick around in the form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerId, scopeType]);

  const isFirstRateRender = useRef(true);
  useEffect(() => {
    if (isFirstRateRender.current) {
      isFirstRateRender.current = false;
      return;
    }
    setValue(FIELDS.RATE_SELECTION, "");
    setValue(FIELDS.RATE_AMOUNT, undefined as unknown as number);
    // A previously picked rate is an index into the OLD trainer's prices array — it must not
    // silently resolve against a different trainer's prices after switching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerId]);

  const courseTrainer = trainers.find((x) => x.id === Number(trainerId));
  const fullTrainer = allTrainers.find((x) => x.id === Number(trainerId));

  const trainerOptions: IDropdownOption[] = trainers.map((x) => ({
    value: x.id.toString(),
    label: `${x.firstName} ${x.surname}`,
  }));

  const sessionGroups = useMemo(() => groupPricesBySession(courseTrainer?.prices ?? []), [courseTrainer]);
  const scheduleOptions: IDropdownOption[] = sessionGroups.map((g) => ({
    value: g.courseSessionId.toString(),
    label: `${g.label} — ${g.hours.toFixed(2)} h`,
  }));

  const dayOptions: IDropdownOption[] = useMemo(() => {
    const byDay = new Map<string, string>();
    (courseTrainer?.prices ?? []).forEach((p) => {
      const key = new Date(p.date).toDateString();
      if (!byDay.has(key)) byDay.set(key, formatDate(p.date, false, false, "short"));
    });
    return Array.from(byDay.entries()).map(([value, label]) => ({ value, label }));
  }, [courseTrainer]);

  const rateOptions: IDropdownOption[] = buildRateOptions(fullTrainer);

  const isManualRate = isManualRateSelection(rateSelection ?? "");

  const scopedPrices = useMemo(() => {
    const prices = courseTrainer?.prices ?? [];
    if (Number(scopeType) === COURSE_TRAINER_COST_SCOPE.SESSION)
      return prices.filter((p) => p.courseSessionId === Number(courseSessionId));
    if (Number(scopeType) === COURSE_TRAINER_COST_SCOPE.DAY)
      return prices.filter((p) => new Date(p.date).toDateString() === date);
    return prices;
  }, [courseTrainer, scopeType, courseSessionId, date]);

  const preview =
    rateSelection && rateAmount
      ? calculateCost(scopedPrices, resolveRateType(rateSelection, fullTrainer), Number(rateAmount))
      : null;

  const onSubmitHandler = async (data: FormValues) => {
    const rateType = resolveRateType(data.rateSelection, fullTrainer);
    const dayDate =
      Number(data.scopeType) === COURSE_TRAINER_COST_SCOPE.DAY ? new Date(data.date as string) : undefined;
    if (dayDate) dayDate.setHours(12, 0, 0, 0);
    await onSubmit({
      trainerId: Number(data.trainerId),
      scopeType: Number(data.scopeType) as CreateTrainerCostEntryRequest["scopeType"],
      courseSessionId:
        Number(data.scopeType) === COURSE_TRAINER_COST_SCOPE.SESSION ? Number(data.courseSessionId) : undefined,
      date: dayDate,
      rateType,
      rateAmount: Number(data.rateAmount),
      rateSourceName: isManualRate ? undefined : fullTrainer?.prices?.[Number(data.rateSelection)]?.name,
    });
  };

  return (
    <div className="bg-primary-50 dark:bg-primary-900/30 mb-2 rounded p-2">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[160px] flex-1">
              <PaziaSelect
                name={FIELDS.TRAINER_ID}
                label="Trener"
                options={trainerOptions}
                required
                isClearable={false}
              />
            </div>
            <div className="min-w-[130px]">
              <PaziaSelect name={FIELDS.SCOPE_TYPE} label="Zakres" options={SCOPE_OPTIONS} required isClearable={false} />
            </div>
            {Number(scopeType) === COURSE_TRAINER_COST_SCOPE.SESSION && (
              <div className="min-w-[200px] flex-1">
                <PaziaSelect
                  name={FIELDS.COURSE_SESSION_ID}
                  label="Zjazd"
                  options={scheduleOptions}
                  required
                  isClearable={false}
                />
              </div>
            )}
            {Number(scopeType) === COURSE_TRAINER_COST_SCOPE.DAY && (
              <div className="min-w-[160px]">
                <PaziaSelect name={FIELDS.DATE} label="Dzień" options={dayOptions} required isClearable={false} />
              </div>
            )}
            <div className="min-w-[180px] flex-1">
              <PaziaSelect
                name={FIELDS.RATE_SELECTION}
                label="Stawka"
                options={rateOptions}
                required
                isClearable={false}
                onChange={(opt: IDropdownOption) => {
                  if (!isManualRateSelection(opt.value.toString()) && fullTrainer) {
                    setValue(FIELDS.RATE_AMOUNT, fullTrainer.prices[Number(opt.value)].amount);
                  }
                }}
              />
            </div>
            <div className="w-28">
              <PaziaInput name={FIELDS.RATE_AMOUNT} label="Kwota" type="number" step="0.01" required disabled={!isManualRate} />
            </div>
            <div className="mb-1 flex gap-2">
              <button type="submit" className="rounded bg-green-600 p-2 text-white hover:bg-green-700" title="Zapisz">
                <MdCheck size={18} />
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded bg-slate-200 p-2 text-slate-700 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-200"
                title="Anuluj"
              >
                <MdClose size={18} />
              </button>
            </div>
          </div>
          {preview && (
            <div className="text-primary-700 dark:text-primary-200 mt-1 text-end text-sm">
              {preview.quantity !== null && <>Ilość: {preview.quantity.toFixed(2)} &mdash; </>}
              Koszt: <span className="font-semibold">{toMoneyFormat(preview.total)} PLN</span>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
